import type { UserRole } from "@supabase-devspace/constants";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";

import { createContext, useContext, useEffect, useState } from "react";

import supabase, { supabaseCustomSignOut } from "@/supabase";

interface UserProfile {
  role: UserRole | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  /** Resolves with the signed-in user's id. */
  signIn: (email: string, password: string) => Promise<string>;
  signOut: () => Promise<void>;
  isLoading: boolean;
  isProfileLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface Props {
  children: React.ReactNode;
}

export function AuthProvider({ children }: Props) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserProfile = async (userId: string) => {
    setIsProfileLoading(true);
    const { data, error: profileError } = await supabase.from("profiles").select("role").eq("user_id", userId).single();

    if (profileError) {
      console.error("Error fetching user profile:", profileError);
      setUserProfile(null);
    } else {
      setUserProfile({ role: data?.role ?? null });
    }
    setIsProfileLoading(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session?.user?.id) {
        await fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setTimeout(async () => {
        setSession(session);
        if (session?.user?.id) {
          await fetchUserProfile(session.user.id);
        } else {
          setUserProfile(null);
        }

        if (event === "SIGNED_OUT") {
          queryClient.clear();
        }

        setIsLoading(false);
      }, 0);
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message);
        throw signInError;
      }

      setSession(data.session);
      const userId = data.session?.user?.id;
      if (!userId) {
        throw new Error("Signed in without a user session");
      }

      await fetchUserProfile(userId);
      setError(null);
      return userId;
    } catch (err) {
      if (err instanceof Error && err.message) {
        setError(err.message);
      } else {
        setError("Failed to sign in");
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await supabaseCustomSignOut();
      setSession(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sign out");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    isAuthenticated: !!session,
    user: session?.user ?? null,
    session,
    userProfile,
    signIn,
    signOut,
    isLoading,
    isProfileLoading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
